import { add, isAfter, isBefore } from 'date-fns'
import { useStorage } from '@vueuse/core'

import useCdcProps from '@/composables/useCdcProps'
import useContextStore from '@/stores/context'
import useProductsStore from '@/stores/products'
import useSubscriptionsStore from '@/stores/subscriptions'
import useService from '@/composables/useServices'
import { Product } from '@/types/products.interfaces'
import { ProductState } from '@/types/products.mbo.interfaces'
import { Subscriptions, Subscription } from '@/types/subscriptions.interfaces'
import { nextTick } from 'vue'

type ActionExecutor = 'shop' | 'mboApi'

export enum PurchasedProductsState {
  unstarted,
  loading,
  loaded,
}

export default function () {
  const contextStore = useContextStore()
  const { productService } = useService()
  const productsStore = useProductsStore()
  const subscriptionsStore = useSubscriptionsStore()
  const { xprops } = useCdcProps()

  const moduleDefaultValue: Product[] = []
  const subscriptionsDefaultValue: Subscriptions = {
    subscriptions: [] as Subscription[],
    has_warnings: false,
    warning_type: undefined,
    warning_count: 0,
    hide_banner: false,
    warning_total_fees: 0,
  }

  const storage = useStorage(
    `purchased-modules-${xprops.context.accounts_shop_id}`,
    {
      expiredAt: new Date(),
      modules: moduleDefaultValue,
      subscriptions: subscriptionsDefaultValue,
    },
  )

  type FetchPurchasedModulesOptions = {
    forceApi?: boolean
    withLoading?: boolean
  }

  async function fetchPurchasedModules(
    opts: FetchPurchasedModulesOptions = {},
  ) {
    const { forceApi = false, withLoading = true } = opts

    const actionExecutor: ActionExecutor =
      xprops?.context.mbo_version < '3.0.0' ? 'shop' : 'mboApi'

    const hasCredential: boolean =
      contextStore.isConnectedToAccount || contextStore.isConnectedToAddons

    if (hasCredential || actionExecutor === 'shop') {
      try {
        if (withLoading)
          productsStore.setPurchasedProductsState(
            PurchasedProductsState.loading,
          )
        if (
          forceApi ||
          isAfter(new Date(), new Date(storage.value.expiredAt))
        ) {
          const modules = await fetchPurchasedModulesFromApi(forceApi)
          return modules
        } else {
          const modules = await fetchPurchasedModulesFromStorage()
          return modules
        }
      } catch (err) {
        console.error(err)
      } finally {
        if (withLoading)
          productsStore.setPurchasedProductsState(PurchasedProductsState.loaded)
      }
    }
  }

  async function fetchPurchasedModulesFromStorage() {
    await nextTick()
    productsStore.setStateInBatch(
      storage.value.modules.map(
        (module) =>
          ({
            name: module.technical_info.technical_name,
            purchased: true,
          }) as ProductState,
      ),
    )
    subscriptionsStore.setInBatch({
      subscriptions: storage.value.subscriptions.subscriptions,
      has_warnings: storage.value.subscriptions.has_warnings,
      warning_type: storage.value.subscriptions.warning_type,
      warning_count: storage.value.subscriptions.warning_count,
      warning_total_fees: storage.value.subscriptions.warning_total_fees,
      hide_banner: storage.value.subscriptions.hide_banner,
    })
    return storage.value.modules
  }

  async function fetchPurchasedModulesFromApi(overwriteStore = false) {
    const {
      modules,
      subscriptions,
      has_warnings,
      warning_type,
      warning_count,
      warning_total_fees,
    } = await productService.fetchPurchased()
    let hide_banner = false
    if (isBefore(new Date(), new Date(storage.value.expiredAt)))
      hide_banner = storage.value.subscriptions.hide_banner

    storage.value.modules = modules
    storage.value.subscriptions = {
      subscriptions,
      has_warnings,
      warning_type,
      warning_count,
      warning_total_fees,
      hide_banner,
    }
    storage.value.expiredAt = add(new Date(), { hours: 6 })
    if (overwriteStore) productsStore.setInBatch(modules)
    productsStore.setStateInBatch(
      modules.map(
        (module) =>
          ({
            name: module.technical_info.technical_name,
            purchased: true,
          }) as ProductState,
      ),
    )
    subscriptionsStore.setInBatch({
      subscriptions,
      has_warnings,
      warning_type,
      warning_count,
      warning_total_fees,
      hide_banner,
    })
    return modules
  }

  function addPurchasedModuleToStorage(module: Product) {
    storage.value.modules = [...storage.value.modules, module]
    storage.value.expiredAt = add(new Date(), { hours: 6 })
  }

  function setHideBanner(hide_banner: boolean) {
    subscriptionsStore.hide_banner = hide_banner
    storage.value.subscriptions.hide_banner = hide_banner
  }

  return {
    fetchPurchasedModules,
    addPurchasedModuleToStorage,
    setHideBanner,
  }
}
